/**
 * Rule-based AI recommendations for hermit-μDC configurator
 *
 * This provides intelligent recommendations when the LLM backend
 * is unavailable (e.g., in demo mode or offline).
 */

import { DatacenterConfig, ScenarioType, NodeType, WizardStep, ComputeRole, NodeSpecs } from '../types/configurator';
import { SCENARIO_PRESETS } from '../data/scenarios';

export interface UserIntent {
  type: 'scenario' | 'nodes' | 'network' | 'services' | 'security' | 'help' | 'redundancy' | 'unknown';
  scenario?: ScenarioType;
  nodeType?: NodeType;
  nodeCount?: number;
  workload?: 'ml' | 'storage' | 'daq' | 'general' | 'industrial';
  question?: string;
  keywords: string[];
}

export interface RecommendationAction {
  type: 'setScenario' | 'addNode' | 'goToStep' | 'updateNetwork' | 'updateSecurity';
  value?: ScenarioType;
  nodeType?: NodeType;
  specs?: Partial<NodeSpecs>;
  role?: ComputeRole;
  step?: WizardStep;
  updates?: Record<string, unknown>;
}

export interface Recommendation {
  message: string;
  actions?: RecommendationAction[];
  suggestions?: string[];
}

/**
 * Parse user message to extract intent
 */
export function parseUserIntent(message: string): UserIntent {
  const lower = message.toLowerCase();
  const words = lower.split(/\s+/);

  const intent: UserIntent = {
    type: 'unknown',
    keywords: words,
  };

  // Detect scenario intent
  if (
    lower.includes('scenario') ||
    lower.includes('start') ||
    lower.includes('new') ||
    lower.includes('create') ||
    lower.includes('setup') ||
    lower.includes('configure')
  ) {
    intent.type = 'scenario';

    if (lower.includes('minimal') || lower.includes('small') || lower.includes('poc') || lower.includes('test')) {
      intent.scenario = 'minimal';
    } else if (lower.includes('lab') || lower.includes('bench') || lower.includes('experiment')) {
      intent.scenario = 'lab-bench';
    } else if (
      lower.includes('research') ||
      lower.includes('ml') ||
      lower.includes('machine learning') ||
      lower.includes('gpu') ||
      lower.includes('cluster') ||
      lower.includes('compute')
    ) {
      intent.scenario = 'research';
      intent.workload = 'ml';
    } else if (
      lower.includes('industrial') ||
      lower.includes('factory') ||
      lower.includes('scada') ||
      lower.includes('ot')
    ) {
      intent.scenario = 'industrial';
      intent.workload = 'industrial';
    }
  }

  // Detect node intent
  if (
    lower.includes('node') ||
    lower.includes('add') ||
    lower.includes('more') ||
    lower.includes('server')
  ) {
    intent.type = 'nodes';

    if (lower.includes('gateway') || lower.includes('router')) {
      intent.nodeType = 'gateway';
    } else if (lower.includes('compute') || lower.includes('worker')) {
      intent.nodeType = 'compute';
    } else if (lower.includes('storage') || lower.includes('disk') || lower.includes('minio')) {
      intent.nodeType = 'storage';
    } else if (lower.includes('daq') || lower.includes('sensor') || lower.includes('acquisition')) {
      intent.nodeType = 'daq';
    }

    // Extract count
    const countMatch = lower.match(/(\d+)\s*(more|nodes?|servers?)/);
    if (countMatch) {
      intent.nodeCount = parseInt(countMatch[1], 10);
    }
  }

  // Detect network intent
  if (
    lower.includes('network') ||
    lower.includes('ip') ||
    lower.includes('subnet') ||
    lower.includes('dns') ||
    lower.includes('domain')
  ) {
    intent.type = 'network';
  }

  // Detect security intent
  if (
    lower.includes('vpn') ||
    lower.includes('security') ||
    lower.includes('encrypt') ||
    lower.includes('firewall') ||
    lower.includes('peer')
  ) {
    intent.type = 'security';
  }

  // Detect redundancy intent
  if (
    lower.includes('redundan') ||
    lower.includes('failover') ||
    lower.includes('high availability') ||
    lower.includes('ha ') ||
    lower.includes('backup gateway') ||
    lower.includes('secondary') ||
    lower.includes('eliminate single point')
  ) {
    intent.type = 'redundancy';
  }

  // Detect help intent
  if (
    lower.includes('help') ||
    lower.includes('what') ||
    lower.includes('how') ||
    lower.includes('explain') ||
    lower.startsWith('?')
  ) {
    intent.type = 'help';
    intent.question = message;
  }

  return intent;
}

/**
 * Generate a recommendation based on user intent and current config
 */
export function generateRecommendation(
  intent: UserIntent,
  config: DatacenterConfig
): Recommendation {
  switch (intent.type) {
    case 'scenario':
      return handleScenarioIntent(intent, config);
    case 'nodes':
      return handleNodesIntent(intent, config);
    case 'network':
      return handleNetworkIntent(intent, config);
    case 'security':
      return handleSecurityIntent(intent, config);
    case 'redundancy':
      return handleRedundancyIntent(intent, config);
    case 'help':
      return handleHelpIntent(intent, config);
    default:
      return handleUnknownIntent(intent, config);
  }
}

function handleScenarioIntent(intent: UserIntent, _config: DatacenterConfig): Recommendation {
  if (intent.scenario) {
    const preset = SCENARIO_PRESETS.find((s) => s.id === intent.scenario);
    if (preset) {
      return {
        message: `Great choice! The **${preset.name}** scenario is perfect for ${intent.workload || 'your needs'}.

This configuration includes:
- ${preset.nodeCount} nodes
- ~${preset.estimatedPower}W power consumption
- ~$${preset.estimatedCost} estimated cost

Use cases: ${preset.useCases.slice(0, 2).join(', ')}

I've selected this scenario for you. You can proceed to customize the network settings.`,
        actions: [
          { type: 'setScenario', value: intent.scenario },
          { type: 'goToStep', step: 'network' },
        ],
        suggestions: [
          'Customize the network settings',
          'Add more compute nodes',
          'Review security options',
        ],
      };
    }
  }

  // No specific scenario detected, recommend based on keywords
  let recommendation = 'Based on your needs, here are my recommendations:\n\n';

  if (intent.workload === 'ml') {
    recommendation += '**Research Cluster** - Best for ML workloads with GPU support and ample storage.\n';
  } else if (intent.workload === 'industrial') {
    recommendation += '**Industrial OT** - Designed for factory automation and SCADA systems.\n';
  } else if (intent.keywords.some((k) => ['sensor', 'data', 'acquisition'].includes(k))) {
    recommendation += '**Lab Bench** - Great for experiments with DAQ support.\n';
  } else {
    recommendation += '**Minimal** - Perfect for getting started or proof-of-concept.\n';
  }

  recommendation += '\nWould you like me to set this up?';

  return {
    message: recommendation,
    suggestions: SCENARIO_PRESETS.map((s) => `Set up ${s.name} scenario`),
  };
}

function handleNodesIntent(intent: UserIntent, config: DatacenterConfig): Recommendation {
  const nodeType = intent.nodeType || 'compute';
  const count = intent.nodeCount || 1;

  if (nodeType === 'gateway' && config.nodes.some((n) => n.type === 'gateway')) {
    return {
      message: "You already have a gateway node configured. Each datacenter needs exactly one gateway for routing and services. Would you like to modify the existing gateway instead?",
      suggestions: ['Modify gateway settings', 'Add compute node instead'],
    };
  }

  const actions: RecommendationAction[] = [];
  for (let i = 0; i < count; i++) {
    actions.push({
      type: 'addNode',
      nodeType,
      role: nodeType === 'compute' ? 'worker' : undefined,
    });
  }

  const typeLabels: Record<NodeType, string> = {
    gateway: 'gateway',
    compute: 'compute',
    storage: 'storage',
    daq: 'DAQ controller',
  };

  return {
    message: `I've added ${count} ${typeLabels[nodeType]} node${count > 1 ? 's' : ''} to your configuration.

Current totals:
- Nodes: ${config.nodes.length + count}
- Power: ~${config.estimatedPower + count * 15}W
- Cost: ~$${Math.round(config.estimatedCost + count * 150)}

You can customize each node's specs in the Nodes step.`,
    actions,
    suggestions: [
      'Add storage node',
      'Configure node specs',
      'Review service allocations',
    ],
  };
}

function handleNetworkIntent(_intent: UserIntent, config: DatacenterConfig): Recommendation {
  return {
    message: `Your current network configuration:

- **Subnet**: ${config.network.subnet}
- **Gateway**: ${config.network.gateway}
- **Domain**: ${config.network.domain}

The network is configured for complete airgap isolation:
- No default route to external networks
- No upstream DNS (local resolution only)
- VPN is the only remote access method

Would you like to change any of these settings?`,
    actions: [{ type: 'goToStep', step: 'network' }],
    suggestions: [
      'Change the domain name',
      'Modify IP addressing',
      'View DHCP settings',
    ],
  };
}

function handleSecurityIntent(_intent: UserIntent, config: DatacenterConfig): Recommendation {
  const vpnStatus = config.security.vpnEnabled ? 'enabled' : 'disabled';
  const peerCount = config.security.peers.length;

  let message = `**Security Configuration**

- VPN: ${vpnStatus} (port ${config.security.vpnListenPort})
- Authorized Peers: ${peerCount}
- Storage Encryption: ${config.security.encryptStorage ? 'LUKS enabled' : 'disabled'}
- Audit Logging: ${config.security.auditLogging ? 'enabled' : 'disabled'}
- Firewall: ${config.security.firewallDefaultDeny ? 'default deny' : 'permissive'}

`;

  if (peerCount === 0 && config.security.vpnEnabled) {
    message += '**Warning**: VPN is enabled but no peers are configured. You need to add at least one authorized peer to access the datacenter remotely.\n\n';
  }

  message += 'The airgap is enforced - all internet egress is blocked by default.';

  return {
    message,
    actions: [{ type: 'goToStep', step: 'security' }],
    suggestions: [
      'Add VPN peer',
      'Enable storage encryption',
      'Review firewall rules',
    ],
  };
}

function handleRedundancyIntent(_intent: UserIntent, config: DatacenterConfig): Recommendation {
  const gatewayCount = config.nodes.filter((n) => n.type === 'gateway').length;
  const storageCount = config.nodes.filter((n) => n.type === 'storage').length;

  const actions: RecommendationAction[] = [];
  const recommendations: string[] = [];

  // Check for gateway redundancy
  if (gatewayCount < 2) {
    recommendations.push('- **Gateway redundancy**: Add a second gateway for failover of DNS, DHCP, and VPN services');
    actions.push({
      type: 'addNode',
      nodeType: 'gateway',
      specs: { cpuCores: 4, memoryGB: 4, storageGB: 64, powerWatts: 5 },
    });
  } else {
    recommendations.push('- **Gateway**: Already redundant with ' + gatewayCount + ' gateways');
  }

  // Check for storage redundancy
  if (storageCount < 2 && config.nodes.length > 2) {
    recommendations.push('- **Storage redundancy**: Consider adding a second storage node for data replication');
    actions.push({
      type: 'addNode',
      nodeType: 'storage',
      specs: { cpuCores: 4, memoryGB: 8, storageGB: 4000, powerWatts: 25 },
    });
  } else if (storageCount >= 2) {
    recommendations.push('- **Storage**: Already redundant with ' + storageCount + ' storage nodes');
  }

  // Build message
  let message = `**Redundancy Analysis**

Current configuration has ${config.nodes.length} nodes total.

${recommendations.join('\n')}

`;

  if (actions.length > 0) {
    message += `\n**Recommended Actions:**
I can add the following nodes to improve redundancy:`;

    for (const action of actions) {
      if (action.type === 'addNode') {
        message += `\n- Add ${action.nodeType} node (${action.specs?.memoryGB}GB RAM, ~${action.specs?.powerWatts}W)`;
      }
    }

    message += '\n\nWould you like me to add these nodes?';
  } else {
    message += '\nYour configuration already has good redundancy.';
  }

  return {
    message,
    actions: actions.length > 0 ? actions : [{ type: 'goToStep', step: 'nodes' }],
    suggestions: [
      'Add more compute nodes',
      'View node configuration',
      'Check power budget',
    ],
  };
}

function handleHelpIntent(intent: UserIntent, _config: DatacenterConfig): Recommendation {
  const question = intent.question?.toLowerCase() || '';

  if (question.includes('airgap')) {
    return {
      message: `**Airgap Isolation**

hermit-μDC is designed to be completely isolated from the internet:

1. **No Default Route**: The gateway has no route to external networks
2. **Local DNS Only**: Only *.hermit.local names resolve
3. **VPN Access**: Operators connect via WireGuard tunnel
4. **Controlled Ingress**: Data enters via sneakernet, data diode, or gated VPN

This ensures sensitive workloads remain protected from network-based attacks.`,
      suggestions: ['Configure VPN peers', 'View security settings'],
    };
  }

  if (question.includes('scenario')) {
    return {
      message: `**Available Scenarios**

1. **Minimal** (~$300, 30W) - 2 nodes for PoC/dev
2. **Lab Bench** (~$600, 80W) - 4 nodes with DAQ
3. **Research Cluster** (~$1500, 150W) - 6 nodes for ML/HPC
4. **Industrial** (~$1200, 120W) - 5 nodes for SCADA/OT

Each scenario pre-configures nodes and services. You can customize after selection.`,
      suggestions: SCENARIO_PRESETS.map((s) => `Select ${s.name}`),
    };
  }

  return {
    message: `I can help you with:

- **Scenarios**: Choose a deployment configuration
- **Nodes**: Add/configure gateway, compute, storage, DAQ
- **Network**: IP addressing, DNS, DHCP settings
- **Services**: Allocate services to nodes
- **Security**: VPN, encryption, audit logging

What would you like to know more about?`,
    suggestions: [
      'Explain airgap isolation',
      'What scenarios are available?',
      'How do I add nodes?',
    ],
  };
}

function handleUnknownIntent(_intent: UserIntent, config: DatacenterConfig): Recommendation {
  return {
    message: `I'm not sure I understood that. Here's what I can help with:

**Quick Actions:**
- "Set up a research cluster" - Configure for ML workloads
- "Add 2 compute nodes" - Expand compute capacity
- "Enable encryption" - Turn on LUKS storage encryption

**Current Configuration:**
- Scenario: ${config.scenario}
- Nodes: ${config.nodes.length}
- Power: ${config.estimatedPower}W

Try asking about scenarios, nodes, network, or security settings.`,
    suggestions: [
      'Set up minimal scenario',
      'Add compute node',
      'Configure VPN access',
    ],
  };
}
